import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon, EyeIcon, CursorArrowRaysIcon, ChartBarIcon, RectangleGroupIcon
} from '@heroicons/react/24/outline';
import { Card, Stats, Loading, Badge } from '../../components/Common';
import api from '../../services/api';

const AdStatsView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ad, setAd] = useState(null);
  const [stats, setStats] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [adRes, statsRes] = await Promise.all([
        api.get(`/advertisements/${id}`),
        api.get(`/advertisements/${id}/stats`),
      ]);
      setAd(adRes.data.data);
      setStats(statsRes.data.data);
    } catch (err) {
      console.error('Failed to fetch ad stats:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const parseJsonArray = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') try { return JSON.parse(val); } catch { return []; }
    return [];
  };

  if (loading) return <Loading fullScreen />;
  if (!ad) return <div className="p-8 text-center text-slate-500">Advertisement not found</div>;

  const targetPages = parseJsonArray(ad.target_pages);
  const targetCategories = parseJsonArray(ad.target_categories);
  const ctr = stats?.ctr || (ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(2) + '%' : '0%');

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/advertisements')} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
          <ArrowLeftIcon className="w-5 h-5 text-slate-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{ad.name}</h1>
          <p className="text-slate-500">Advertisement statistics and performance</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stats title="Total Impressions" value={(stats?.impressions || ad.impressions || 0).toLocaleString()} icon={EyeIcon} iconBg="bg-indigo-100" iconColor="text-indigo-600" />
        <Stats title="Total Clicks" value={(stats?.clicks || ad.clicks || 0).toLocaleString()} icon={CursorArrowRaysIcon} iconBg="bg-purple-100" iconColor="text-purple-600" />
        <Stats title="Click-Through Rate" value={ctr} icon={ChartBarIcon} iconBg="bg-emerald-100" iconColor="text-emerald-600" />
        <Stats title="Priority" value={ad.priority || 0} icon={ChartBarIcon} iconBg="bg-cyan-100" iconColor="text-cyan-600" />
      </div>

      <Card title="Advertisement Details">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="space-y-1">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Slot</p>
            <p className="font-medium text-slate-800 capitalize">{ad.slot?.replace(/-/g, ' ')}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Type</p>
            <div className="flex items-center gap-2">
              <p className="font-medium text-slate-800">{ad.advertisementType?.name || 'N/A'}</p>
              {ad.advertisementType?.description && (
                <span className="text-xs text-slate-400">— {ad.advertisementType.description}</span>
              )}
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Format</p>
            <div className="flex items-center gap-2">
              <RectangleGroupIcon className="w-4 h-4 text-slate-400" />
              <p className="font-medium text-slate-800">
                {ad.advertisementFormat
                  ? `${ad.advertisementFormat.name}${ad.advertisementFormat.width && ad.advertisementFormat.height ? ` (${ad.advertisementFormat.width}\u00D7${ad.advertisementFormat.height}px)` : ''}`
                  : 'Auto'}
              </p>
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Status</p>
            <Badge variant={ad.is_active ? 'success' : 'danger'}>{ad.is_active ? 'Active' : 'Inactive'}</Badge>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Target URL</p>
            <p className="font-medium text-slate-800 truncate">{ad.target_url || 'N/A'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Schedule</p>
            <p className="font-medium text-slate-800">
              {ad.start_date ? new Date(ad.start_date).toLocaleDateString() : 'No start'} — {ad.end_date ? new Date(ad.end_date).toLocaleDateString() : 'No end'}
            </p>
          </div>
        </div>
      </Card>

      {(targetPages.length > 0 || targetCategories.length > 0) && (
        <Card title="Targeting">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {targetPages.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-700">Target Pages</p>
                <div className="flex flex-wrap gap-2">
                  {targetPages.map((page, i) => (
                    <span key={i} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded-full">{page}</span>
                  ))}
                </div>
              </div>
            )}
            {targetCategories.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-700">Target Categories</p>
                <div className="flex flex-wrap gap-2">
                  {targetCategories.map((cat, i) => (
                    <span key={i} className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded-full">Category #{cat}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default AdStatsView;
